/**
 * Procedural surface textures, generated on a canvas at boot — plank wood, checker
 * tile and mottled concrete for floors; striped wallpaper and flat paint for walls,
 * both finished with a pale baseboard and picture rail on full-height walls.
 * Deliberately naive and cartoon-flat, in the *Hello Neighbor* spirit: color
 * variation and pattern, no photorealism, and zero texture assets in the bundle.
 *
 * Several of these are not surfaces at all: the exterior backdrop, the near woods, the rain
 * shell and the moon's halo are the world outside, and they live here because they are drawn
 * the same way — on a canvas, in code, from a seed. See `ExteriorWorld` and `Moon` for how
 * they are hung in space. `hedgeBumpCanvas` is not even an image — it is a height field for
 * the hedge's leaf clumps, drawn here because it has to agree with the hedge's colour tile.
 *
 * All randomness is seeded from the pattern and palette color (see `seededRandom`): the
 * same profile renders the same house down to the wood grain, build after build.
 */

import { Color } from 'three';
import { treeline } from '../game/profile/buildExterior';
import type { GroundPoint, PathSegment, Rect } from '../game/profile/types';
import { mulberry32, seedFromString } from '../utils/seededRandom';

const TEXTURE_SIZE = 256;

export type SurfacePattern = 'wood' | 'tile' | 'concrete' | 'wallpaper' | 'paint' | 'hedge' | 'water';

/** The base color, nudged lighter (>1) or darker (<1) without changing hue. */
function shade(base: string, factor: number): string {
  const hsl = { h: 0, s: 0, l: 0 };
  new Color(base).getHSL(hsl);
  const lightness = Math.min(1, Math.max(0, hsl.l * factor));
  return `#${new Color().setHSL(hsl.h, hsl.s, lightness).getHexString()}`;
}

type Ctx = CanvasRenderingContext2D;

/** Floor planks: four boards per 2 m tile, seams, end joints and grain streaks. */
function drawWood(context: Ctx, color: string, random: () => number): void {
  const plankWidth = TEXTURE_SIZE / 4;

  for (let plank = 0; plank < 4; plank++) {
    const x = plank * plankWidth;
    context.fillStyle = shade(color, 0.92 + random() * 0.16);
    context.fillRect(x, 0, plankWidth, TEXTURE_SIZE);

    context.strokeStyle = shade(color, 0.8);
    context.globalAlpha = 0.25;
    for (let streak = 0; streak < 5; streak++) {
      const streakX = x + 4 + random() * (plankWidth - 8);
      context.beginPath();
      context.moveTo(streakX, random() * TEXTURE_SIZE * 0.5);
      context.lineTo(streakX + (random() - 0.5) * 6, TEXTURE_SIZE * (0.5 + random() * 0.5));
      context.stroke();
    }
    context.globalAlpha = 1;

    context.fillStyle = shade(color, 0.65);
    context.fillRect(x, 0, 2, TEXTURE_SIZE);
    context.fillRect(x, Math.floor(random() * TEXTURE_SIZE), plankWidth, 2);
  }
}

/** Kitchen-style checker tiles: 4×4 per 2 m tile with grout and per-tile tint. */
function drawTile(context: Ctx, color: string, random: () => number): void {
  const tile = TEXTURE_SIZE / 4;
  const light = shade(color, 1.25);

  for (let row = 0; row < 4; row++) {
    for (let column = 0; column < 4; column++) {
      const base = (row + column) % 2 === 0 ? color : light;
      context.fillStyle = shade(base, 0.97 + random() * 0.06);
      context.fillRect(column * tile, row * tile, tile, tile);
    }
  }

  // Grout: a dark grid over the lot.
  context.strokeStyle = shade(color, 0.55);
  context.lineWidth = 3;
  for (let line = 0; line <= 4; line++) {
    const at = Math.min(line * tile, TEXTURE_SIZE - 1.5);
    context.beginPath();
    context.moveTo(at, 0);
    context.lineTo(at, TEXTURE_SIZE);
    context.stroke();
    context.beginPath();
    context.moveTo(0, at);
    context.lineTo(TEXTURE_SIZE, at);
    context.stroke();
  }
  context.lineWidth = 1;
}

/** Storage-room concrete: mottled patches, a stain or two, hairline cracks. */
function drawConcrete(context: Ctx, color: string, random: () => number): void {
  context.fillStyle = color;
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  for (let patch = 0; patch < 60; patch++) {
    context.fillStyle = shade(color, 0.85 + random() * 0.3);
    context.globalAlpha = 0.12;
    const size = 8 + random() * 40;
    context.fillRect(random() * TEXTURE_SIZE, random() * TEXTURE_SIZE, size, size * (0.5 + random()));
  }

  context.globalAlpha = 0.1;
  context.fillStyle = shade(color, 0.6);
  for (let stain = 0; stain < 3; stain++) {
    context.beginPath();
    context.ellipse(
      random() * TEXTURE_SIZE,
      random() * TEXTURE_SIZE,
      20 + random() * 30,
      12 + random() * 20,
      random() * Math.PI,
      0,
      Math.PI * 2,
    );
    context.fill();
  }

  context.globalAlpha = 0.3;
  context.strokeStyle = shade(color, 0.5);
  for (let crack = 0; crack < 3; crack++) {
    let x = random() * TEXTURE_SIZE;
    let y = random() * TEXTURE_SIZE;
    context.beginPath();
    context.moveTo(x, y);
    for (let segment = 0; segment < 6; segment++) {
      x += (random() - 0.5) * 40;
      y += random() * 25;
      context.lineTo(x, y);
    }
    context.stroke();
  }
  context.globalAlpha = 1;
}

/** Broad vertical stripes two shades apart, over a light speckle. */
function drawWallpaper(context: Ctx, color: string, random: () => number): void {
  const stripeWidth = TEXTURE_SIZE / 8;

  for (let stripe = 0; stripe < 8; stripe++) {
    context.fillStyle = stripe % 2 === 0 ? color : shade(color, 1.12);
    context.fillRect(stripe * stripeWidth, 0, stripeWidth, TEXTURE_SIZE);
  }

  context.globalAlpha = 0.05;
  context.fillStyle = shade(color, 0.5);
  for (let speck = 0; speck < 220; speck++) {
    context.fillRect(random() * TEXTURE_SIZE, random() * TEXTURE_SIZE, 2, 2);
  }
  context.globalAlpha = 1;
}

/** Flat paint: the color with a faint cloudy mottle so it does not read as plastic. */
function drawPaint(context: Ctx, color: string, random: () => number): void {
  context.fillStyle = color;
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  for (let cloud = 0; cloud < 40; cloud++) {
    context.fillStyle = shade(color, 0.94 + random() * 0.12);
    context.globalAlpha = 0.08;
    const size = 20 + random() * 60;
    context.beginPath();
    context.ellipse(
      random() * TEXTURE_SIZE,
      random() * TEXTURE_SIZE,
      size,
      size * 0.6,
      random() * Math.PI,
      0,
      Math.PI * 2,
    );
    context.fill();
  }
  context.globalAlpha = 1;
}

/**
 * A handful of discrete shade steps rather than a continuous range — the leaf mass reads
 * as flat-shaded clusters instead of a smooth gradient, in keeping with the banded toon
 * ramp everything else is lit with (see `getGradientMap` in `materials.ts`).
 *
 * A clump takes *three adjacent* steps: one below for its shaded lobe, its own for the
 * body, one above for the lit lobe. That is what keeps the fake per-clump depth on the
 * same ladder as the real lighting instead of inventing a second, smoother one — so the
 * body index is only ever drawn from the middle of the range (see `HEDGE_BODY_STEPS`).
 */
const HEDGE_SHADE_STEPS = [0.6, 0.8, 1, 1.2, 1.4];
/** Which of those a clump body may use, so `step ± 1` is always in range. */
const HEDGE_BODY_STEPS = [1, 2, 3];

/** How many leaf clumps cover the tile before their wrapped twins are added. */
const HEDGE_CLUSTER_COUNT = 170;

/**
 * One leaf clump on the hedge tile, in normalised 0..1 texture space.
 *
 * Extracted from the drawing for the same reason `rainStreaks` is: the arithmetic is the
 * part that has to be right, and the part jsdom can actually run. It also has a second
 * job here that the rain does not — the colour tile and the *bump* tile must agree clump
 * for clump, or the moonlight catches relief that is not where the leaves are painted. One
 * list, generated once, drawn twice.
 */
export type HedgeCluster = {
  /** Centre of the clump. */
  x: number;
  y: number;
  /** Radius of the body lobe, as a fraction of the tile. */
  radius: number;
  /** How far the lit and shaded lobes sit either side of the centre, in radii. */
  offset: number;
  /** Ellipse squash and its tilt, so no clump is a plain circle. */
  squash: number;
  tilt: number;
};

/**
 * Where the leaf clumps sit.
 *
 * Wrapped in u only, and deliberately. `MazeGrounds` maps exactly one tile across each
 * drawn hedge piece, so the tile's left and right edges are where two neighbouring pieces
 * of the same run meet — a clump cut off at one edge has to reappear at the other or the
 * hedge is visibly stitched together every three metres. Down the tile there is no such
 * seam to hide: v runs from the footing to the clipped top of the hedge *once*, and the
 * ambient-occlusion gradient baked in below means this tile is not vertically tileable at
 * all, on purpose.
 */
export function hedgeClusters(
  seed = 'hedge',
  count = HEDGE_CLUSTER_COUNT,
): HedgeCluster[] {
  const random = mulberry32(seedFromString(`hedge-clusters:${seed}`));
  const clusters: HedgeCluster[] = [];

  for (let clump = 0; clump < count; clump++) {
    const cluster: HedgeCluster = {
      x: random(),
      y: random(),
      radius: 0.028 + random() * 0.042,
      offset: 0.3 + random() * 0.25,
      squash: 0.62 + random() * 0.3,
      tilt: random() * Math.PI,
    };
    clusters.push(cluster);

    // The twin a tile away, so the two vertical edges of the tile carry the same clumps.
    const reach = cluster.radius * (1 + cluster.offset);
    if (cluster.x - reach < 0) clusters.push({ ...cluster, x: cluster.x + 1 });
    else if (cluster.x + reach > 1) clusters.push({ ...cluster, x: cluster.x - 1 });
  }

  return clusters;
}

/**
 * The direction the lit lobe of every clump sits in, in canvas space (y grows *down*), so
 * up-and-left. Every clump shares it: a hedge lit from one side is a hedge, a hedge whose
 * clumps each pick their own sun is a rock wall.
 */
const HEDGE_LIGHT_X = -0.7;
const HEDGE_LIGHT_Y = -0.7;

/** Draws one lobe of a clump at `(x, y)` pushed `push` px along `(dx, dy)`. */
function drawClusterLobe(
  context: Ctx,
  cluster: HedgeCluster,
  push: number,
  dx: number,
  dy: number,
  radius: number,
  style: string,
): void {
  context.fillStyle = style;
  context.beginPath();
  context.ellipse(
    cluster.x * TEXTURE_SIZE + dx * push,
    cluster.y * TEXTURE_SIZE + dy * push,
    radius,
    radius * cluster.squash,
    cluster.tilt,
    0,
    Math.PI * 2,
  );
  context.fill();
}

/** A shaded palette colour as `rgba(...)`, which canvas gradients need for per-stop alpha. */
function shadeAlpha(base: string, factor: number, alpha: number): string {
  const hex = parseInt(shade(base, factor).slice(1), 16);
  return `rgba(${(hex >> 16) & 255}, ${(hex >> 8) & 255}, ${hex & 255}, ${alpha})`;
}

/**
 * Leaf mass: clumps with fake depth over a dark interior.
 *
 * The first version of this was uniform speckle, and uniform speckle is what a painted
 * green wall looks like. Two things fix that, both free at render time because they are
 * baked here (design D10):
 *
 * - Each clump is drawn as a *pair* of lobes around a body — one shade darker pushed away
 *   from the light, one lighter pushed towards it. That is a lie about geometry that the
 *   eye reads as a clump standing proud of the mass, and it costs nothing on the GPU.
 * - The base under them is much darker than the leaves, so the gaps between clumps read
 *   as depth into the mass rather than as more of it.
 *
 * This is the shared half. A grounds level uses this pattern twice — the hedges *and* the
 * lawn are leaves — and only the hedges want the third thing D10 asks for, the baked
 * footing shadow. That is a gradient down one tile, and a lawn tiles the pattern every two
 * metres, so baking it in here would stripe the grass. It lives in `hedgeFaceCanvas`.
 */
function drawHedge(context: Ctx, color: string, random: () => number): void {
  // The shade between the leaves — well below anything a clump is painted with, so a gap
  // reads as a hole into the hedge.
  context.fillStyle = shade(color, 0.45);
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  for (const cluster of hedgeClusters()) {
    const body = HEDGE_BODY_STEPS[Math.floor(random() * HEDGE_BODY_STEPS.length)]!;
    const radius = cluster.radius * TEXTURE_SIZE;
    const push = radius * cluster.offset;

    // Shaded lobe first and widest, then the body over it, then the lit lobe on top: the
    // dark survives only as a crescent on the far side, which is what a lit clump does.
    drawClusterLobe(context, cluster, push, -HEDGE_LIGHT_X, -HEDGE_LIGHT_Y, radius,
      shade(color, HEDGE_SHADE_STEPS[body - 1]!));
    drawClusterLobe(context, cluster, 0, 0, 0, radius * 0.92,
      shade(color, HEDGE_SHADE_STEPS[body]!));
    drawClusterLobe(context, cluster, push, HEDGE_LIGHT_X, HEDGE_LIGHT_Y, radius * 0.55,
      shade(color, HEDGE_SHADE_STEPS[body + 1]!));
  }

  // Individual leaves over the clumps — enough to break the lobes' clean ellipses, far
  // fewer than the flat speckle this replaced, which drowned them out.
  context.globalAlpha = 0.5;
  for (let leaf = 0; leaf < 240; leaf++) {
    context.fillStyle = shade(color, HEDGE_SHADE_STEPS[Math.floor(random() * HEDGE_SHADE_STEPS.length)]!);
    const size = 1.5 + random() * 3;
    context.beginPath();
    context.ellipse(
      random() * TEXTURE_SIZE,
      random() * TEXTURE_SIZE,
      size,
      size * (0.6 + random() * 0.4),
      random() * Math.PI,
      0,
      Math.PI * 2,
    );
    context.fill();
  }

  // Crevices: a few dark punctures so the mass is not uniformly clumped either.
  context.globalAlpha = 0.3;
  context.fillStyle = shade(color, 0.35);
  for (let crevice = 0; crevice < 90; crevice++) {
    context.beginPath();
    context.arc(random() * TEXTURE_SIZE, random() * TEXTURE_SIZE, 1.5 + random() * 3, 0, Math.PI * 2);
    context.fill();
  }
  context.globalAlpha = 1;
}

/** One short bright dash, drawn (and re-drawn a tile away) so it never leaves a seam. */
function drawWaterHighlight(context: Ctx, x: number, y: number, length: number, alpha: number): void {
  context.globalAlpha = alpha;
  context.beginPath();
  context.moveTo(x, y);
  context.lineTo(x + length, y);
  context.stroke();

  if (y - length < 0) {
    context.beginPath();
    context.moveTo(x, y + TEXTURE_SIZE);
    context.lineTo(x + length, y + TEXTURE_SIZE);
    context.stroke();
  } else if (y + length > TEXTURE_SIZE) {
    context.beginPath();
    context.moveTo(x, y - TEXTURE_SIZE);
    context.lineTo(x + length, y - TEXTURE_SIZE);
    context.stroke();
  }
  context.globalAlpha = 1;
}

/** How many full sine cycles a ripple band completes across the tile's width. */
const WATER_RIPPLE_CYCLES = 3;

/** One wavy horizontal ripple band, sampled at whole sine cycles so it tiles in x too. */
function drawRippleBand(
  context: Ctx,
  centerY: number,
  amplitude: number,
  phase: number,
  lineWidth: number,
  style: string,
): void {
  context.strokeStyle = style;
  context.lineWidth = lineWidth;
  context.beginPath();
  for (let x = 0; x <= TEXTURE_SIZE; x += 4) {
    const wave = Math.sin((x / TEXTURE_SIZE) * Math.PI * 2 * WATER_RIPPLE_CYCLES + phase);
    const y = centerY + wave * amplitude;
    if (x === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  }
  context.stroke();
}

/**
 * Moat water: gentle horizontal ripple bands and a scatter of broken highlights.
 *
 * This is the one pattern that gets a continuous scrolling offset at render time (see
 * `openspec/changes/hedge-maze-level/design.md` D4, and the identical technique on the
 * rain shell in `ExteriorWorld.tsx`) — a seam here does not sit still like it would on a
 * static wall, it visibly crawls up the moat forever. So every feature is wrap-safe: the
 * ripple bands are sampled at whole sine cycles, and any band or highlight that reaches
 * the top or bottom edge gets a second copy drawn a full tile away, exactly the way
 * `rainStreaks` wraps a streak that overruns the bottom.
 */
function drawWater(context: Ctx, color: string, random: () => number): void {
  context.fillStyle = shade(color, 0.9);
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  context.globalAlpha = 0.18;
  const bandCount = 10;
  for (let band = 0; band < bandCount; band++) {
    const centerY = (band / bandCount) * TEXTURE_SIZE;
    const amplitude = 3 + random() * 4;
    const lineWidth = 2 + random() * 2;
    const phase = random() * Math.PI * 2;
    const style = shade(color, band % 2 === 0 ? 1.18 : 0.75);

    drawRippleBand(context, centerY, amplitude, phase, lineWidth, style);
    // Only a band centered at (or near) an edge can reach past it — draw its wrapped
    // twin so the top and bottom edges carry the exact same waveform.
    const reach = amplitude + lineWidth;
    if (centerY - reach < 0) drawRippleBand(context, centerY + TEXTURE_SIZE, amplitude, phase, lineWidth, style);
    if (centerY + reach > TEXTURE_SIZE) drawRippleBand(context, centerY - TEXTURE_SIZE, amplitude, phase, lineWidth, style);
  }
  context.globalAlpha = 1;

  context.strokeStyle = shade(color, 1.45);
  context.lineCap = 'round';
  for (let highlight = 0; highlight < 50; highlight++) {
    const x = random() * TEXTURE_SIZE;
    const y = random() * TEXTURE_SIZE;
    const length = 4 + random() * 10;
    const alpha = 0.12 + random() * 0.18;
    drawWaterHighlight(context, x, y, length, alpha);
  }
}

const DRAWERS: Record<SurfacePattern, (context: Ctx, color: string, random: () => number) => void> = {
  wood: drawWood,
  tile: drawTile,
  concrete: drawConcrete,
  wallpaper: drawWallpaper,
  paint: drawPaint,
  hedge: drawHedge,
  water: drawWater,
};

const canvases = new Map<string, HTMLCanvasElement | null>();

/** One streak of rain on the storm tile, in normalised 0..1 texture space. */
export type RainStreak = { x: number; y: number; length: number; alpha: number };

const RAIN_COUNT = 90;

/**
 * Where the rain falls on the storm tile.
 *
 * Kept separate from the drawing because the drawing needs a canvas and this needs to be
 * right: the tile scrolls forever, so a streak crossing the bottom edge must be drawn
 * again at the top or a bare seam marches up every window once per cycle.
 */
export function rainStreaks(seed: string, count = RAIN_COUNT): RainStreak[] {
  const random = mulberry32(seedFromString(`rain:${seed}`));
  const streaks: RainStreak[] = [];

  for (let drop = 0; drop < count; drop++) {
    const streak: RainStreak = {
      x: random(),
      y: random(),
      length: 0.06 + random() * 0.13,
      alpha: 0.16 + random() * 0.3,
    };
    streaks.push(streak);
    // The wrapped copy, so the tile joins itself.
    if (streak.y + streak.length > 1) streaks.push({ ...streak, y: streak.y - 1 });
  }

  return streaks;
}

/**
 * The backdrop is wider than everything else here, and deliberately not repeated.
 *
 * Every other texture in this file tiles, because a repeat in wood grain or rain is
 * unreadable. A skyline is not like that: repeating it around the horizon would put the
 * same three trees outside the south window and the west one, which is precisely the
 * consistency this exterior exists to provide. One skyline, drawn once, all the way round.
 *
 * 1024 x 512 is 2 MB on the GPU — small beside the shadow maps already allocated, and the
 * only texture in the project that needs to hold up when the player looks straight at it.
 */
const EXTERIOR_WIDTH = 1024;
const EXTERIOR_HEIGHT = 512;

/**
 * Colours for the world outside, at night in a storm. Read top to bottom: the sky darkens
 * with altitude, brightens into haze at the horizon, and the ground below is wet enough to
 * be nearly black.
 */
const NIGHT_SKY_TOP = '#0d131d';
const NIGHT_SKY_HORIZON = '#2c3849';
/**
 * The glow above the treeline, and the reason this change does not make windows fail the
 * "brightest thing on its wall" requirement. Night woods are darker than the flat storm
 * texture they replace; without a band of light behind them, a window in a room whose lamp
 * is off stops reading as a way out. This is a colour doing a light's job — deliberately,
 * because the alternative is adding a light the mobile budget cannot afford.
 */
const NIGHT_HAZE = '#4a5b73';
const TREE_COLOR = '#0a1016';
/**
 * Black, flat, for now.
 *
 * It had the faintest blue at the horizon on the theory that wet ground at night still
 * catches some sky at a grazing angle. In the room it read as haze rather than ground, and
 * gave the eye a surface where there should be nothing. Unlit black is what actually looks
 * like the far side of a window at night.
 *
 * Two constants rather than one so a gradient can come back without hunting for the call
 * sites — and both the painted band on the backdrop and the ground plane in `ExteriorWorld`
 * read from the first, so the two can never drift apart and show a seam at the horizon.
 */
export const GROUND_COLOR = '#000000';
const GROUND_COLOR_FAR = '#000000';

/** Where the horizon sits, as a fraction of the canvas height. */
const HORIZON = 0.5;
/** How tall the tallest tree stands above the horizon, as a fraction of canvas height. */
const TREE_SCALE = 0.11;

/**
 * The rows of woods, furthest first.
 *
 * One row of trees reads as a fence: a single silhouette with a hard top edge and nothing
 * behind it. Real woods recede, and the four things that say so are all here — a far row
 * is shorter (`scale`), fainter against the haze (`tone`), made of narrower trees
 * (`spread`) and more of them (`count`), and its bases sit on the horizon while nearer
 * rows are rooted progressively below it (`drop`) so they overlap what is behind.
 */
type TreeRow = { scale: number; tone: number; spread: number; count: number; drop: number };

/**
 * The rows painted onto the backdrop. These recede by tone and scale but cannot slide
 * against each other — they are all the same distance away.
 */
const TREE_ROWS: readonly TreeRow[] = [
  { scale: 0.42, tone: 0.34, spread: 0.6, count: 260, drop: 0 },
  { scale: 0.66, tone: 0.6, spread: 0.85, count: 190, drop: 0.011 },
];

/**
 * The nearest row, which is *not* painted on the backdrop. It goes on its own sphere
 * closer to the house (see `buildExterior`), so it slides against the ridge behind it as
 * the player crosses a room. Bigger and blacker than anything on the backdrop, because it
 * is genuinely nearer rather than merely drawn to look it.
 */
const NEAR_TREE_ROW: TreeRow = { scale: 1, tone: 1, spread: 1.25, count: 120, drop: 0.027 };

/**
 * One row of woods along the horizon.
 *
 * The horizon is a loop and both spheres map it the same way — `v` linear in polar angle,
 * equator at the canvas midpoint — so a row drawn here lands at the same world height
 * whichever sphere it ends up on.
 */
function drawTreeRow(context: Ctx, row: TreeRow, seed: string, horizonY: number): void {
  const baseY = horizonY + row.drop * EXTERIOR_HEIGHT;

  context.fillStyle = TREE_COLOR;
  for (const spike of treeline(seed, row.count)) {
    const centerX = spike.at * EXTERIOR_WIDTH;
    const halfWidth = spike.halfWidth * row.spread * EXTERIOR_WIDTH;
    const top = baseY - spike.height * row.scale * TREE_SCALE * EXTERIOR_HEIGHT;

    // Within a row the taller trees are the nearer ones, so they darken too.
    context.globalAlpha = row.tone * (0.62 + spike.height * 0.38);
    context.beginPath();
    context.moveTo(centerX - halfWidth, baseY + 2);
    context.lineTo(centerX, top);
    context.lineTo(centerX + halfWidth, baseY + 2);
    context.closePath();
    context.fill();
  }
  context.globalAlpha = 1;
}

/**
 * The near woods alone, on nothing — the transparent sphere that gives the forest real
 * parallax instead of painted perspective.
 */
export function nearWoodsCanvas(seed: string): HTMLCanvasElement | null {
  const key = `near-woods:${seed}`;
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = EXTERIOR_WIDTH;
  canvas.height = EXTERIOR_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  drawTreeRow(context, NEAR_TREE_ROW, `${seed}:near-woods`, EXTERIOR_HEIGHT * HORIZON);

  canvases.set(key, canvas);
  return canvas;
}

/** How much of the halo's radius is the bright inner glow before it starts falling away. */
const HALO_CORE = 0.28;
const HALO_COLOR = '226, 236, 255';

/**
 * The moon's halo: a radial fall-off from a pale glow to nothing.
 *
 * A disc of flat colour at a low opacity would be a soft *tone* with a hard *edge*, and the
 * edge is what gives it away — a circle of exactly 12% white against a night sky reads as a
 * cut-out, not as light. The gradient is the whole point, and a canvas is the cheapest place
 * to get one: no shader, no second material, no per-frame anything.
 *
 * Not seeded and not per-colour: there is one moon.
 */
export function moonHaloCanvas(): HTMLCanvasElement | null {
  const cached = canvases.get('moon-halo');
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set('moon-halo', null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set('moon-halo', null);
    return null;
  }

  const middle = TEXTURE_SIZE / 2;
  const glow = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  glow.addColorStop(0, `rgba(${HALO_COLOR}, 0.5)`);
  glow.addColorStop(HALO_CORE, `rgba(${HALO_COLOR}, 0.22)`);
  // Squared-off rather than linear: light falls away faster than a straight ramp does, and
  // a linear one still has a visible rim where it finally reaches zero.
  glow.addColorStop(0.6, `rgba(${HALO_COLOR}, 0.05)`);
  glow.addColorStop(1, `rgba(${HALO_COLOR}, 0)`);
  context.fillStyle = glow;
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  canvases.set('moon-halo', canvas);
  return canvas;
}

/**
 * How far up the tile the footing shadow reaches, and how dark it gets at the very bottom.
 * A real hedge sits in its own shade: the light that reaches the leaves at ankle height has
 * come past everything above them.
 */
const HEDGE_AO_HEIGHT = 0.55;
const HEDGE_AO_DEPTH = 0.6;

/**
 * A hedge's own tile: the leaf mass, plus the ambient occlusion a standing hedge bakes into
 * itself — dark at the footing, a touch brighter where the sky reaches the top.
 *
 * Separate from `surfaceCanvas('hedge', …)` because the gradient is only meaningful where
 * one tile spans one hedge from grass to clipped top, which is exactly how `MazeGrounds`
 * maps it. The grass of the same level is the same leaf pattern tiled every two metres, and
 * a vertical gradient tiled every two metres is a lawn in stripes.
 *
 * The orientation is the part that has to be right, and it is worth writing down. Canvas y
 * grows *downward*, `CanvasTexture` flips the image on upload, and a `BoxGeometry`'s side
 * faces put v = 1 at the top of the box (checked against three.js's own UV layout, not
 * assumed). So the bottom rows of this canvas are the bottom of the hedge, and darkening
 * them darkens the footing — the way round it has to be.
 *
 * The *top* face of a hedge samples the same tile, with v running across the 0.6 m
 * thickness, so one edge of that narrow strip picks up the dark end of the gradient. It
 * reads as the shaded side of the top roll, it is only visible at the apex of a jump, and
 * the alternative — a second material for that face — would break the single instanced draw
 * this whole level is built around. Left alone deliberately.
 */
export function hedgeFaceCanvas(color: string): HTMLCanvasElement | null {
  const key = `hedge-face:${color}`;
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  // The same seed the pattern would get on its own, so hedge and lawn are the same foliage.
  drawHedge(context, color, mulberry32(seedFromString(`hedge:${color}`)));

  const footing = TEXTURE_SIZE * (1 - HEDGE_AO_HEIGHT);
  const ao = context.createLinearGradient(0, footing, 0, TEXTURE_SIZE);
  ao.addColorStop(0, shadeAlpha(color, 0.2, 0));
  ao.addColorStop(0.5, shadeAlpha(color, 0.2, HEDGE_AO_DEPTH * 0.28));
  ao.addColorStop(1, shadeAlpha(color, 0.2, HEDGE_AO_DEPTH));
  context.fillStyle = ao;
  context.fillRect(0, footing, TEXTURE_SIZE, TEXTURE_SIZE - footing);

  const crown = context.createLinearGradient(0, 0, 0, TEXTURE_SIZE * 0.22);
  crown.addColorStop(0, shadeAlpha(color, 1.5, 0.18));
  crown.addColorStop(1, shadeAlpha(color, 1.5, 0));
  context.fillStyle = crown;
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE * 0.22);

  canvases.set(key, canvas);
  return canvas;
}

/**
 * The hedge's height field: the same clumps as the colour tile, as grey mounds.
 *
 * Why a second canvas rather than reusing the colour one as a bump map — which is free and
 * was the first thing tried. Two reasons, both fatal. `MeshToonMaterial` reads a bump map's
 * *red* channel, and a green hedge has almost no red in it, so the relief came out as a
 * faint smear. And the colour tile now carries a baked vertical gradient (the footing
 * shadow above): read as height, that gradient is a constant slope, and every hedge in the
 * maze would be lit as though it leaned backwards.
 *
 * Mid grey is "the surface"; each clump is a soft mound with a darker moat scooped around
 * it, so a leaf clump catches the moon on its own and the gaps between clumps do not. Not
 * per-colour and not per-seed: there is one hedge shape in the game, and it must line up
 * with `hedgeClusters()` clump for clump. No `colorSpace` is set — this is data, not
 * colour, and a bump map put through an sRGB decode is a bump map with the wrong slopes.
 */
export function hedgeBumpCanvas(): HTMLCanvasElement | null {
  const cached = canvases.get('hedge-bump');
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set('hedge-bump', null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set('hedge-bump', null);
    return null;
  }

  context.fillStyle = '#808080';
  context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

  for (const cluster of hedgeClusters()) {
    const x = cluster.x * TEXTURE_SIZE;
    const y = cluster.y * TEXTURE_SIZE;
    const radius = cluster.radius * TEXTURE_SIZE;

    // The moat first, wider than the clump: without it a mound has nothing to stand out
    // of, and the whole tile drifts brighter as the mounds pile up.
    const hollow = context.createRadialGradient(x, y, radius * 0.6, x, y, radius * 1.5);
    hollow.addColorStop(0, 'rgba(0, 0, 0, 0.32)');
    hollow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    context.fillStyle = hollow;
    context.fillRect(x - radius * 1.5, y - radius * 1.5, radius * 3, radius * 3);

    const mound = context.createRadialGradient(x, y, 0, x, y, radius);
    mound.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
    mound.addColorStop(0.55, 'rgba(255, 255, 255, 0.4)');
    mound.addColorStop(1, 'rgba(255, 255, 255, 0)');
    context.fillStyle = mound;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }

  // Leaf-scale grain on top of the clumps, so the relief does not stop at clump size.
  const random = mulberry32(seedFromString('hedge-bump'));
  for (let leaf = 0; leaf < 900; leaf++) {
    const lit = random() < 0.5;
    context.fillStyle = lit ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.16)';
    context.beginPath();
    context.arc(random() * TEXTURE_SIZE, random() * TEXTURE_SIZE, 1 + random() * 2.5, 0, Math.PI * 2);
    context.fill();
  }

  canvases.set('hedge-bump', canvas);
  return canvas;
}

/**
 * The world outside, wrapped once around the sphere the house sits inside.
 *
 * Laid out for a sphere's UVs, which map `v` linearly to the polar angle: the horizon
 * therefore lands exactly halfway up the canvas, sky above it and ground below. That is
 * what lets one flat image be a whole world without any per-window special-casing — and
 * why the image must carry ground at all, since a player at eye height looks *down*
 * through a window whose sill is below them.
 */
export function exteriorCanvas(seed: string): HTMLCanvasElement | null {
  const key = `exterior:${seed}`;
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = EXTERIOR_WIDTH;
  canvas.height = EXTERIOR_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  const horizonY = EXTERIOR_HEIGHT * HORIZON;
  const random = mulberry32(seedFromString(`exterior:${seed}`));

  const sky = context.createLinearGradient(0, 0, 0, horizonY);
  sky.addColorStop(0, NIGHT_SKY_TOP);
  sky.addColorStop(1, NIGHT_SKY_HORIZON);
  context.fillStyle = sky;
  context.fillRect(0, 0, EXTERIOR_WIDTH, horizonY);

  // Cloud base, so the sky is not a clean ramp. Low and flat, as storm cloud is.
  context.globalAlpha = 0.1;
  context.fillStyle = '#7d8ea6';
  for (let cloud = 0; cloud < 26; cloud++) {
    context.beginPath();
    context.ellipse(
      random() * EXTERIOR_WIDTH,
      random() * horizonY * 0.85,
      60 + random() * 160,
      10 + random() * 26,
      0,
      0,
      Math.PI * 2,
    );
    context.fill();
  }
  context.globalAlpha = 1;

  // The haze above the treeline. Painted rather than fogged: scene fog ends at 45 m and
  // would erase the backdrop outright, and tuning it would mean changing how the far end
  // of every interior room looks.
  const haze = context.createLinearGradient(0, horizonY - EXTERIOR_HEIGHT * 0.22, 0, horizonY);
  haze.addColorStop(0, 'rgba(74, 91, 115, 0)');
  haze.addColorStop(1, NIGHT_HAZE);
  context.fillStyle = haze;
  context.fillRect(0, horizonY - EXTERIOR_HEIGHT * 0.22, EXTERIOR_WIDTH, EXTERIOR_HEIGHT * 0.22);

  // Wet ground, darkening away from the horizon.
  const ground = context.createLinearGradient(0, horizonY, 0, EXTERIOR_HEIGHT);
  ground.addColorStop(0, GROUND_COLOR);
  ground.addColorStop(1, GROUND_COLOR_FAR);
  context.fillStyle = ground;
  context.fillRect(0, horizonY, EXTERIOR_WIDTH, EXTERIOR_HEIGHT - horizonY);

  // The far woods, drawn furthest row first so each nearer one overlaps the last. Depth
  // between *these* rows is aerial perspective — they share a sphere, so they cannot slide
  // against each other. The row that does slide is on its own sphere: `nearWoodsCanvas`.
  for (const [index, row] of TREE_ROWS.entries()) {
    drawTreeRow(context, row, `${seed}:row-${index}`, horizonY);
  }

  canvases.set(key, canvas);
  return canvas;
}

/**
 * Rain alone, on nothing: transparent everywhere the drops are not.
 *
 * Wrapped around a cylinder standing outside the house, this is a curtain of rain hanging
 * in the air at a real distance rather than a pattern on a pane. Two of them at different
 * radii is what gives the storm depth — see `ExteriorWorld`.
 */
export function rainShellCanvas(): HTMLCanvasElement | null {
  const cached = canvases.get('rain-shell');
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set('rain-shell', null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set('rain-shell', null);
    return null;
  }

  context.strokeStyle = '#dbe8f7';
  context.lineWidth = 1.5;
  context.lineCap = 'round';
  for (const streak of rainStreaks('shell')) {
    context.globalAlpha = streak.alpha;
    context.beginPath();
    context.moveTo(streak.x * TEXTURE_SIZE, streak.y * TEXTURE_SIZE);
    // Slightly off vertical, as though driven by wind.
    context.lineTo((streak.x + 0.012) * TEXTURE_SIZE, (streak.y + streak.length) * TEXTURE_SIZE);
    context.stroke();
  }
  context.globalAlpha = 1;

  canvases.set('rain-shell', canvas);
  return canvas;
}

/**
 * How far the trodden path's centreline may wander off its leg, in metres.
 *
 * The corridor is 2.4 m wide (`CELL_SIZE - HEDGE_THICKNESS`) and the mud is drawn about
 * 1.3 m wide, so 0.45 m of wander keeps the mud's edge a boot's width clear of the hedges
 * it runs between — the path may brush a hedge footing, never disappear under one.
 */
export const PATH_MEANDER_MAX = 0.45;

/** Roughly how far apart the meander's waypoints sit along a leg, in metres. */
const PATH_STEP = 0.8;

/**
 * The waypoints one leg of the path is drawn through: the straight leg, bent sideways by
 * seeded noise that fades to nothing at both ends.
 *
 * The fade is what lets legs be meandered independently: every leg starts and ends at a
 * cell centre it shares with its neighbours, so pinning the ends makes the whole network
 * chain up with no seams, without this function ever knowing the network exists. Seeded
 * per leg from the leg's own coordinates — architectural rule 4, and it is what keeps the
 * path identical build after build.
 */
export function pathMeander(seed: string, leg: PathSegment): GroundPoint[] {
  const dx = leg.to.x - leg.from.x;
  const dz = leg.to.z - leg.from.z;
  const length = Math.hypot(dx, dz);
  const random = mulberry32(
    seedFromString(`${seed}:${leg.from.x},${leg.from.z}:${leg.to.x},${leg.to.z}`),
  );

  const steps = Math.max(2, Math.round(length / PATH_STEP));
  const points: GroundPoint[] = [leg.from];
  for (let step = 1; step < steps; step += 1) {
    const t = step / steps;
    // Perpendicular to the leg, scaled by a sine window so the ends stay pinned.
    const wander = (random() * 2 - 1) * PATH_MEANDER_MAX * Math.sin(Math.PI * t);
    points.push({
      x: leg.from.x + dx * t + (dz / length) * wander,
      z: leg.from.z + dz * t - (dx / length) * wander,
    });
  }
  points.push(leg.to);
  return points;
}

/**
 * How big the one grounds canvas is. Unlike every tile above, this image covers the whole
 * lawn exactly once — the mud has to follow the maze's actual corridors, and a repeating
 * tile cannot know where they are. 1024² over ~48 m is ~21 px/m: soft at your feet, which
 * grass survives far better than wallpaper would, and a quarter of the memory the sun's
 * shadow map already spends.
 */
const GROUNDS_SIZE = 1024;

/**
 * What mud is, like `HEDGE_COLOR` in `MazeGrounds`: not a palette decision. The palette
 * colour is the *grass*; the path is the same trodden brown on any lawn.
 */
const MUD_COLOR = '#57452e';

/**
 * The lawn with its trodden path: grass mottle everywhere, and mud drawn along the maze's
 * corridor legs — wide worn grass first, then the mud, then a darker trodden centre, each
 * leg meandered by `pathMeander`.
 */
export function groundsCanvas(
  color: string,
  rect: Rect,
  paths: readonly PathSegment[],
): HTMLCanvasElement | null {
  const key = `grounds:${color}:${rect.width}x${rect.depth}:${paths.length}`;
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = GROUNDS_SIZE;
  canvas.height = GROUNDS_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  const random = mulberry32(seedFromString(key));
  const toX = (x: number) => ((x - rect.x) / rect.width) * GROUNDS_SIZE;
  const toY = (z: number) => ((z - rect.z) / rect.depth) * GROUNDS_SIZE;
  const metre = GROUNDS_SIZE / rect.width;

  // The grass: the palette colour knocked down, then mottled in broad soft patches and a
  // fine speckle so the lawn reads as growth rather than paint.
  context.fillStyle = shade(color, 0.88);
  context.fillRect(0, 0, GROUNDS_SIZE, GROUNDS_SIZE);

  for (let patch = 0; patch < 160; patch += 1) {
    context.fillStyle = shade(color, 0.72 + random() * 0.5);
    context.globalAlpha = 0.09;
    const radius = (1 + random() * 3.5) * metre;
    context.beginPath();
    context.ellipse(
      random() * GROUNDS_SIZE,
      random() * GROUNDS_SIZE,
      radius,
      radius * (0.5 + random() * 0.5),
      random() * Math.PI,
      0,
      Math.PI * 2,
    );
    context.fill();
  }
  context.globalAlpha = 0.3;
  for (let speck = 0; speck < 5000; speck += 1) {
    context.fillStyle = shade(color, 0.6 + random() * 0.8);
    const size = 1 + random() * 2;
    context.fillRect(random() * GROUNDS_SIZE, random() * GROUNDS_SIZE, size, size);
  }
  context.globalAlpha = 1;

  // The path. Three strokes per leg, widest first — worn grass, mud, trodden centre —
  // with round caps so legs meeting at a cell centre blend instead of showing corners.
  context.lineCap = 'round';
  context.lineJoin = 'round';
  const passes: Array<{ width: number; style: string; alpha: number }> = [
    { width: 1.9, style: shade(color, 0.55), alpha: 0.6 },
    { width: 1.3, style: MUD_COLOR, alpha: 0.92 },
    { width: 0.6, style: shade(MUD_COLOR, 0.75), alpha: 0.55 },
  ];
  for (const pass of passes) {
    context.strokeStyle = pass.style;
    context.globalAlpha = pass.alpha;
    context.lineWidth = pass.width * metre;
    for (const leg of paths) {
      const points = pathMeander('trodden', leg);
      context.beginPath();
      for (const [index, point] of points.entries()) {
        if (index === 0) context.moveTo(toX(point.x), toY(point.z));
        else context.lineTo(toX(point.x), toY(point.z));
      }
      context.stroke();
    }
  }

  // Mud blotches along the legs, so the path's edge is ragged rather than a clean stripe.
  context.globalAlpha = 0.4;
  context.fillStyle = MUD_COLOR;
  for (const leg of paths) {
    const length = Math.hypot(leg.to.x - leg.from.x, leg.to.z - leg.from.z);
    const blotches = Math.max(1, Math.round(length));
    for (let blotch = 0; blotch < blotches; blotch += 1) {
      const t = random();
      const radius = (0.2 + random() * 0.35) * metre;
      context.beginPath();
      context.ellipse(
        toX(leg.from.x + (leg.to.x - leg.from.x) * t) + (random() * 2 - 1) * metre * 0.8,
        toY(leg.from.z + (leg.to.z - leg.from.z) * t) + (random() * 2 - 1) * metre * 0.8,
        radius,
        radius * (0.5 + random() * 0.5),
        random() * Math.PI,
        0,
        Math.PI * 2,
      );
      context.fill();
    }
  }
  context.globalAlpha = 1;

  canvases.set(key, canvas);
  return canvas;
}

/**
 * The tileable pattern for a surface, or null where canvases cannot draw (tests,
 * SSR) — callers then use a plain colored material instead.
 */
export function surfaceCanvas(pattern: SurfacePattern, color: string): HTMLCanvasElement | null {
  const key = `${pattern}:${color}`;
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  DRAWERS[pattern](context, color, mulberry32(seedFromString(`${pattern}:${color}`)));

  canvases.set(key, canvas);
  return canvas;
}

/**
 * The exit door's red paint: a pair of panelled leaves, drawn flat and graphic in the same
 * cartoon register as the rest of this module.
 *
 * A *double* door, because the opening it fills is the whole mouth of a maze corridor — about
 * 3.25 m across and 2.65 m tall — and a single leaf stretched over that is a barn door with a
 * doorknob. Two leaves make the width honest.
 *
 * The canvas is square and the panel it maps onto is not, so everything here comes out about 20%
 * wider in the world than it is drawn. That is deliberately left alone: a door's stiles and
 * panels have no canonical proportion a player could catch us breaking, and matching the aspect
 * exactly would mean a non-square canvas for no gain.
 */
export const DOOR_RED = '#a8302a';
const DOOR_SHADOW = '#7d1f1b';
const DOOR_PANEL = '#be3f36';
const DOOR_BRASS = '#d8bd74';

export function exitDoorCanvas(): HTMLCanvasElement | null {
  const key = 'exit-door';
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  const size = TEXTURE_SIZE;
  context.fillStyle = DOOR_RED;
  context.fillRect(0, 0, size, size);

  // The meeting stile down the middle and a jamb's width of shadow at each edge: what tells
  // the eye this is two leaves hung in an opening rather than one flat board.
  const stile = size * 0.035;
  context.fillStyle = DOOR_SHADOW;
  context.fillRect(size / 2 - stile / 2, 0, stile, size);
  context.fillRect(0, 0, stile, size);
  context.fillRect(size - stile, 0, stile, size);
  context.fillRect(0, size - stile, size, stile);

  // Two sunk panels per leaf. Each is a lighter field with a dark top-left and a light
  // bottom-right edge — the cheapest honest way to say "recessed" without a normal map.
  const margin = size * 0.085;
  const leaf = size / 2;
  const panelWidth = leaf - 2 * margin;
  const panelHeight = (size - 3 * margin) / 2;
  for (const side of [0, 1]) {
    for (const row of [0, 1]) {
      const x = side * leaf + margin;
      const y = margin + row * (panelHeight + margin);
      context.fillStyle = DOOR_PANEL;
      context.fillRect(x, y, panelWidth, panelHeight);
      context.fillStyle = DOOR_SHADOW;
      context.fillRect(x, y, panelWidth, stile * 0.6);
      context.fillRect(x, y, stile * 0.6, panelHeight);
      context.fillStyle = shade(DOOR_PANEL, 1.25);
      context.fillRect(x, y + panelHeight - stile * 0.6, panelWidth, stile * 0.6);
      context.fillRect(x + panelWidth - stile * 0.6, y, stile * 0.6, panelHeight);
    }
  }

  // Two handles, one each side of the meeting stile, at the height a hand reaches.
  const handleY = size * 0.52;
  const handleLength = size * 0.075;
  context.fillStyle = DOOR_BRASS;
  for (const direction of [-1, 1]) {
    const from = size / 2 + direction * (stile * 1.4);
    context.fillRect(
      Math.min(from, from + direction * handleLength),
      handleY,
      handleLength,
      size * 0.022,
    );
  }

  canvases.set(key, canvas);
  return canvas;
}

/**
 * How many times wider than tall the exit sign is — and therefore the aspect of its canvas.
 *
 * This is the one canvas in the module that is not square, and it has to be: the sign is a long
 * low panel, and lettering drawn on a square canvas stretched across it would come out four
 * times too wide. Matching the aspect is what keeps the word legible, which is the sign's entire
 * job. `PortalGate` sizes the panel from the same constant, so the two cannot drift apart.
 */
export const EXIT_SIGN_ASPECT = 4;

/**
 * The green exit sign that hangs over the door: a bordered green panel with the word on it.
 *
 * Green with white lettering, the way an emergency exit sign is everywhere a player has ever
 * seen one. The *word* is what carries the meaning — principle 4, no information in colour
 * alone — so the type is as large as the panel will take, and the material it goes on is unlit
 * (see `PortalGate`) so the moonlit maze cannot dim it.
 *
 * The typeface is whatever the device has: a generic sans-serif stack, no font asset and no text
 * renderer in the bundle for four letters. A substituted face changes the letterform and not the
 * word.
 */
export const SIGN_GREEN = '#1a9e4b';
const SIGN_INK = '#f2fff5';

export function exitSignCanvas(): HTMLCanvasElement | null {
  const key = 'exit-sign';
  const cached = canvases.get(key);
  if (cached !== undefined) return cached;

  if (typeof document === 'undefined') {
    canvases.set(key, null);
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE / EXIT_SIGN_ASPECT;
  const context = canvas.getContext('2d');
  if (!context) {
    canvases.set(key, null);
    return null;
  }

  const width = canvas.width;
  const height = canvas.height;
  context.fillStyle = SIGN_GREEN;
  context.fillRect(0, 0, width, height);

  const border = height * 0.1;
  context.strokeStyle = SIGN_INK;
  context.lineWidth = border;
  context.strokeRect(border, border, width - 2 * border, height - 2 * border);

  context.fillStyle = SIGN_INK;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `bold ${Math.round(height * 0.62)}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  // Nudged down a touch: `middle` sits on the font's own centre, which for a line of capitals
  // leaves it looking high in the panel.
  context.fillText('EXIT', width / 2, height * 0.54);

  canvases.set(key, canvas);
  return canvas;
}
